import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SEO from "@/components/seo";

// Helper to get the correct path based on user type
const getBasePath = () => {
  const userType = localStorage.getItem("userType");
  return userType === 'brand' ? '/brand' : '';
};
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API_ROOT } from "@/config";
import AnalysisSidebar from "@/components/dashboard";
import { Check, X, FilePenLine, ArrowLeft, AlertCircle, ChevronDown, ChevronUp, Lightbulb, Loader2, TrendingUp, Info, Play, Pause, Volume2, VolumeX, Maximize, Clock, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import CollapsedProfile from "@/components/ui/collapsed-profile";
import FeatureRequestButton from "@/components/ui/feature-request-button";
import React from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { HelpCircle } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const FoldedSidebar = ({ onShowSidebar }: { onShowSidebar: () => void }) => {
  const navigate = useNavigate();

  const handleNewAnalysisClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`${getBasePath()}/upload`);
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ 
        background: PALETTE.brightSnow, 
        width: 56, 
        borderRight: `1px solid ${PALETTE.americanSilver}` 
      }}
    >
      {/* Fixed Top Section - matching expanded sidebar */}
      <div className="flex-shrink-0">
        {/* Header with logo - same position as expanded */}
        <div className="flex items-center justify-center px-4 py-3">
          <div
            onClick={onShowSidebar}
            title="Expand Sidebar"
            className="cursor-pointer"
          >
            <img 
              src="/brandcomply_logo.png" 
              alt="brandcomply logo" 
              className="h-6 w-6"
            />
          </div>
        </div>

        {/* New Analysis Button - matching position */}
        <div
          onClick={handleNewAnalysisClick}
          className="flex items-center justify-center mx-4 my-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:opacity-80"
          style={{ 
            background: PALETTE.inchworm,
            color: PALETTE.gunmetal
          }}
          title="New Check"
        >
          <FilePenLine className="w-4 h-4 flex-shrink-0" />
        </div>


      </div>

      {/* Spacer to push profile to bottom */}
      <div className="flex-1"></div>

      {/* Fixed Bottom Section - Profile matching expanded sidebar */}
      <div className="flex-shrink-0">
        <div className="flex justify-center p-2">
          <CollapsedProfile />
        </div>
      </div>
    </div>
  );
};

const getScoreGradient = (score: number) => {
  if (score >= 90) return { start: PALETTE.inchworm, end: '#22c55e' }; // Green: 90-100%
  if (score >= 61) return { start: '#facc15', end: '#fbbf24' }; // Yellow: 61-89%
  return { start: '#ff4d6d', end: '#f43f5e' }; // Red: 0-60%
};

const ComplianceScoreChart = ({ score, size = 96 }: { score: number | null; size?: number }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeWidth = 8;

  // If score is null, show N/A in grey
  if (score === null) {
    return (
      <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
        <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={PALETTE.americanSilver}
            strokeWidth={strokeWidth}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold" style={{ color: PALETTE.gunmetal }}>N/A</span>
        </div>
      </div>
    );
  }

  const strokeDashoffset = circumference - (score / 100) * circumference;
  const gradient = getScoreGradient(score);
  const gradientId = `scoreGradient-${Math.round(score)}`;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={PALETTE.americanSilver}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle with gradient */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradient.start} />
            <stop offset="100%" stopColor={gradient.end} />
          </linearGradient>
        </defs>
      </svg>
      {/* Score text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: PALETTE.gunmetal }}>{Math.round(score)}%</span>
      </div>
    </div>
  );
};

// Circular Compliance Score Overlay Component
const ComplianceScoreOverlay = ({ 
  currentScore
}: { 
  currentScore: number | null; 
}) => {
  const current = Math.round(currentScore ?? 0);
  
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (current / 100) * circumference;
  
  const getScoreColor = (score: number) => {
    if (score >= 90) return PALETTE.inchworm;
    if (score >= 61) return '#fbbf24';
    return '#f43f5e';
  };

  const scoreColor = getScoreColor(current);
  
  if (currentScore === null) return null;
  
  return (
        <div
      className="absolute top-4 right-4 z-20 flex flex-col items-center gap-2 p-3 rounded-full backdrop-blur-sm"
            style={{
        background: 'rgba(255, 255, 255, 0.75)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div className="relative w-20 h-20">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={PALETTE.americanSilver}
            strokeWidth="6"
            opacity="0.3"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={scoreColor}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tracking-tight" style={{ color: PALETTE.gunmetal }}>
            {current}%
          </span>
      </div>
        </div>
    </div>
  );
};

// Generic requirement item from external API
interface RequirementItem {
  input: string;
  complies: boolean;
  ai_answer?: string;
  confidence?: number;
  timestamp?: number;
}

// Analysis result - flexible structure to match external API
interface Analysis {
  unique_id: string;
  created_at: number;
  input: {
    campaignName: string;
    brandName?: string;
    exactProduct?: string;
    transcriptLanguage?: string;
    briefing_data?: any;
    // Legacy fields for backward compatibility
    dosSeen?: string[];
    dosHeard?: string[];
    dontsSeen?: string[];
    dontsHeard?: string[];
  };
  compliance_score?: number;
  // External API format
  compliant?: Array<any>;  // Array of compliant requirements
  non_compliant?: Array<any>;  // Array of non-compliant requirements
  total_requirements?: number;
  template_id?: string;
  payload_echo?: any;
  video_path?: string;
  processing_duration_sec?: number;
  job_id?: string;
  // Alternative formats
  requirements?: RequirementItem[];  // Flat list of all requirements
  total_processing_time?: number;
  external_job_id?: string;
  [key: string]: any;  // Allow any additional fields from external API
}

const STEPS = [
  "Briefing Upload",
  "Briefing Review",
  "Video Upload",
  "Compliance Check & Recommendations",
];

// Timeline marker interface
interface TimelineMarker {
  time: number; // in seconds
  requirementNumber: number;
  requirement: string;
  aiAnswer: string;
}

// Helper function to parse timestamps from ai_answer text
// Matches patterns like: 00:09, 00:14-00:15, (00:03-00:05), etc.
function parseTimestampsFromText(text: string): number[] {
  if (!text) return [];
  
  const timestamps: number[] = [];
  // Regex to match mm:ss or hh:mm:ss formats
  const timestampRegex = /(\d{1,2}):(\d{2})(?::(\d{2}))?/g;
  
  let match;
  while ((match = timestampRegex.exec(text)) !== null) {
    const hours = match[3] ? parseInt(match[1]) : 0;
    const minutes = match[3] ? parseInt(match[2]) : parseInt(match[1]);
    const seconds = match[3] ? parseInt(match[3]) : parseInt(match[2]);
    
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    timestamps.push(totalSeconds);
  }
  
  return Array.from(new Set(timestamps)); // Remove duplicates
}

// Format seconds to mm:ss
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getRequirementsTableData(analysisResult: Analysis | null): Array<{ requirement: string; compliance: string; ai_answer?: string; confidence?: number }> {
  const rows: Array<{ requirement: string; compliance: string; ai_answer?: string; confidence?: number }> = [];
  
  if (!analysisResult) {
    console.log("🔍 getRequirementsTableData: No analysis result");
    return rows;
  }
  
  console.log("🔍 getRequirementsTableData analyzing structure:");
  console.log("  - Available keys:", Object.keys(analysisResult));
  
  // Helper function to extract requirement text and remove "Element:" prefix if present
  const extractRequirementText = (item: any): string => {
    const text = item.input || item.requirement || item.text || JSON.stringify(item);
    // Remove "Element:" prefix if present (added by external API)
    if (typeof text === 'string') {
      if (text.startsWith('Element: ')) {
        return text.substring(9).trim(); // Remove "Element: " (9 characters)
      } else if (text.startsWith('Element:')) {
        return text.substring(8).trim(); // Remove "Element:" (8 characters)
      }
    }
    return text;
  };
  
  // PRIORITY 1: Check for external API format (compliant/non_compliant arrays)
  if (analysisResult.compliant && Array.isArray(analysisResult.compliant)) {
    console.log(`  - Found compliant array with ${analysisResult.compliant.length} items`);
    analysisResult.compliant.forEach((item: any) => {
      // Handle different possible structures
      const requirement = extractRequirementText(item);
      const aiAnswer = item.ai_answer || item.feedback || item.explanation;
      rows.push({
        requirement: requirement,
        compliance: "✅",
        ai_answer: aiAnswer,
        confidence: item.confidence
      });
    });
  }
  
  if (analysisResult.non_compliant && Array.isArray(analysisResult.non_compliant)) {
    console.log(`  - Found non_compliant array with ${analysisResult.non_compliant.length} items`);
    analysisResult.non_compliant.forEach((item: any) => {
      // Handle different possible structures
      const requirement = extractRequirementText(item);
      const aiAnswer = item.ai_answer || item.feedback || item.explanation;
      rows.push({
        requirement: requirement,
        compliance: "❌",
        ai_answer: aiAnswer,
        confidence: item.confidence
      });
    });
  }
  
  // PRIORITY 2: Check for flat requirements array (simple format)
  if (rows.length === 0 && analysisResult.requirements && Array.isArray(analysisResult.requirements)) {
    console.log(`  - Found flat requirements array with ${analysisResult.requirements.length} items`);
    analysisResult.requirements.forEach((item: RequirementItem) => {
      rows.push({
        requirement: extractRequirementText(item),
        compliance: item.complies ? "✅" : "❌",
        ai_answer: item.ai_answer,
        confidence: item.confidence
      });
    });
  }
  
  // PRIORITY 3: Recursively search for requirement-like objects
  if (rows.length === 0) {
    console.log("  - No direct arrays found, searching nested structure...");
    const searchForRequirements = (obj: any, path: string = '') => {
      if (!obj || typeof obj !== 'object') return;
      
      // Check if this is a requirement item itself
      if (obj.input && typeof obj.complies === 'boolean') {
        console.log(`  - Found requirement at path: ${path}`);
      rows.push({
          requirement: extractRequirementText(obj),
          compliance: obj.complies ? "✅" : "❌",
          ai_answer: obj.ai_answer,
          confidence: obj.confidence
        });
        return;
      }
      
      // Recursively search nested objects and arrays
      Object.entries(obj).forEach(([key, value]) => {
        // Skip already processed arrays
        if (key === 'compliant' || key === 'non_compliant' || key === 'requirements') return;
        
        if (Array.isArray(value)) {
          value.forEach((item, idx) => searchForRequirements(item, `${path}${key}[${idx}]`));
        } else if (value && typeof value === 'object') {
          searchForRequirements(value, `${path}${key}.`);
        }
      });
    };
    
    searchForRequirements(analysisResult);
  }
  
  console.log(`🔍 getRequirementsTableData found ${rows.length} total requirements`);
  
  return rows;
}

// Text truncation component for long requirements
const TruncatedText = ({ text, maxLength = 200 }: { text: string; maxLength?: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (text.length <= maxLength) {
    return <>{text}</>;
  }
  
  return (
    <>
      {isExpanded ? text : `${text.substring(0, maxLength)}...`}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="ml-2 text-xs font-semibold hover:underline"
        style={{ color: PALETTE.paleViolet }}
      >
        {isExpanded ? 'Show less' : 'Show more'}
      </button>
    </>
  );
};

// Tooltip component for AI answer on hover
const RequirementTooltip = ({ aiAnswer }: { aiAnswer?: string }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  if (!aiAnswer) return null;
  
  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };
  
  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onMouseMove={handleMouseMove}
    >
      <span 
        className="cursor-help text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
        style={{ fontSize: '14px' }}
        title="Hover for AI feedback"
      >
        ℹ️
      </span>
      {showTooltip && (
        <div 
          className="fixed z-[10000] p-3 bg-gray-900 text-white text-sm rounded-lg shadow-xl max-w-sm"
          style={{ 
            pointerEvents: 'none',
            wordWrap: 'break-word',
            left: `${Math.min(mousePos.x + 10, window.innerWidth - 350)}px`,
            top: `${Math.max(mousePos.y - 10, 10)}px`,
            transform: 'translateY(-100%)'
          }}
        >
          <div className="font-semibold mb-1 text-xs">AI Feedback:</div>
          <div className="text-xs">{aiAnswer}</div>
        </div>
      )}
    </div>
  );
};

// AG Grid cell renderers
const NumberCellRenderer = (params: ICellRendererParams) => {
  const isCompliant = params.data?.compliance === '✅';
  return (
    <div 
      className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
      style={{ 
        background: isCompliant ? `${PALETTE.inchworm}20` : '#fee2e2',
        color: isCompliant ? PALETTE.gunmetal : '#f43f5e'
      }}
    >
      {params.value}
    </div>
  );
};

const RequirementCellRenderer = (params: ICellRendererParams) => {
  const text = params.value || '';
  const maxLength = 120;
  const truncated = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  const aiAnswer = params.data?.ai_answer;
  
  return (
    <div 
      className="flex items-center gap-2 h-full w-full cursor-default"
      title={aiAnswer ? `AI Feedback: ${aiAnswer}` : text}
    >
      <span className="flex-1 truncate" style={{ color: PALETTE.gunmetal }}>
        {truncated}
      </span>
      {aiAnswer && (
        <RequirementTooltip aiAnswer={aiAnswer} />
      )}
    </div>
  );
};

const ComplianceIconRenderer = (params: ICellRendererParams) => {
  const isCompliant = params.value === '✅';
  return (
    <div className="flex items-center justify-center h-full">
      {isCompliant ? (
        <Check className="w-5 h-5" style={{ color: PALETTE.inchworm }} />
      ) : (
        <X className="w-5 h-5" style={{ color: '#f43f5e' }} />
      )}
    </div>
  );
};

const VideoResult = () => {
  const { unique_id } = useParams<{ unique_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Campaign mode detection - Initialize synchronously based on URL to avoid flicker
  const isCampaignResultPath = location.pathname.startsWith('/campaign-result/');
  const campaignQueryParam = new URLSearchParams(location.search).get('campaign');
  const userType = localStorage.getItem("userType");
  const isBrand = userType === 'brand';
  
  const [isCampaignMode, setIsCampaignMode] = useState(() => {
    // Initialize synchronously to avoid race condition
    return isCampaignResultPath || !!campaignQueryParam;
  });
  const [campaignUuid, setCampaignUuid] = useState<string | null>(() => campaignQueryParam);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [proofLink, setProofLink] = useState<string | null>(null);
  // For campaign mode, show submission page (AG Grid table) immediately after analysis
  // This page has Re-Upload and Final Submit buttons
  const [showSubmissionPage, setShowSubmissionPage] = useState(() => {
    // Campaign mode users see the submission page with AG Grid and buttons
    // Brand users on /campaign-result/ also see submission page (read-only)
    if (isCampaignResultPath || campaignQueryParam) {
      console.log("🎬 INITIAL showSubmissionPage: TRUE (campaign mode - show AG Grid with buttons)");
      return true;
    }
    console.log("🎬 INITIAL showSubmissionPage: FALSE (normal mode)");
    return false;
  });
  const [creatorComments, setCreatorComments] = useState<Record<number, string>>({});
  const [showSubmissionInfoModal, setShowSubmissionInfoModal] = useState(false);

  // Robust check if this is a creator viewing their own submission (public link)
  const isCreatorView = useMemo(() => {
    return !!campaignQueryParam;
  }, [campaignQueryParam]);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebarOpen");
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Detect campaign mode from URL pathname or query params
  // Note: State is initialized synchronously above, this useEffect handles URL changes
  useEffect(() => {
    const isResultPath = location.pathname.startsWith('/campaign-result/');
    const params = new URLSearchParams(location.search);
    const campaignParam = params.get('campaign');
    const userType = localStorage.getItem("userType");
    const isBrand = userType === 'brand';
    
    console.log("🔍 Campaign mode check:", {
      path: location.pathname, 
      isResultPath, 
      campaignParam,
      userType,
      isBrand,
      currentIsCampaignMode: isCampaignMode,
      currentShowSubmissionPage: showSubmissionPage
    });
    
    // Update campaign mode state and ensure submission page is shown
    if (campaignParam) {
      console.log("✅ Campaign mode: PUBLIC LINK (query param detected)");
      if (!isCampaignMode) setIsCampaignMode(true);
      if (!campaignUuid) setCampaignUuid(campaignParam);
      // Show submission page with AG Grid table and buttons
      if (!showSubmissionPage) setShowSubmissionPage(true);
    } else if (isResultPath) {
      console.log("✅ Campaign mode: BRAND VIEW (/campaign-result/ path)");
      if (!isCampaignMode) setIsCampaignMode(true);
      // Show submission page for brand viewing
      if (!showSubmissionPage) setShowSubmissionPage(true);
    }
  }, [location.pathname, location.search, isCampaignMode, showSubmissionPage, campaignUuid]);
  
  // Extract campaign_uuid from analysis data if in campaign mode
  // Also check submission status and load creator comments
  useEffect(() => {
    if (isCampaignMode && analysis) {
      // Try to get campaign_uuid from analysis result
      const analysisCampaignUuid = (analysis as any).campaign_uuid;
      if (analysisCampaignUuid && !campaignUuid) {
        setCampaignUuid(analysisCampaignUuid);
      }
      
      // Check if submission is already completed
      const submissionStatus = (analysis as any).submission_status;
      
      // Only update state if submission is already completed (someone reloading a submitted analysis)
      if (submissionStatus === 'submitted') {
        setSubmissionComplete(true);
        setShowSubmissionPage(true); // Show submission page for already-submitted results
        
        // Load creator comments if they exist
        const comments = (analysis as any).creator_comments;
        if (comments && Array.isArray(comments)) {
          const commentsMap: Record<number, string> = {};
          comments.forEach((comment: any) => {
            if (comment.requirement_number && comment.comment) {
              commentsMap[comment.requirement_number] = comment.comment;
            }
          });
          setCreatorComments(commentsMap);
        }
      }
      // DO NOT force showSubmissionPage to false for new analyses
      // Let user navigate manually with buttons
    }
  }, [isCampaignMode, analysis, campaignUuid]);

  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] = useState(false);
  const [recommendationsExpanded, setRecommendationsExpanded] = useState(false);
  const hasGeneratedRecommendations = useRef(false);
  
  // Priority filter for recommendations (effort-based)
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'quick_fix' | 'moderate' | 'reshoot'>('all');
  
  // Modal for recommendation details
  const [selectedRecommendation, setSelectedRecommendation] = useState<any | null>(null);
  
  // Debug: Monitor modal state changes
  useEffect(() => {
    console.log('showAnalysisModal state changed to:', showAnalysisModal);
  }, [showAnalysisModal]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [videoHeight, setVideoHeight] = useState<number | null>(null);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const token = localStorage.getItem("token");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Get presigned URL for direct S3 access (better performance)
  useEffect(() => {
    if (unique_id && token) {
      console.log("🔍 FRONTEND DEBUG: Fetching presigned URL for:", unique_id);
      
      const fetchPresignedUrl = async () => {
        try {
          const response = await fetch(`${API_ROOT}/video/${unique_id}/url?token=${token}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.presigned_url) {
              console.log("✅ FRONTEND DEBUG: Got presigned URL for direct S3 access");
              setVideoUrl(data.presigned_url);
            } else if (data.local_storage || data.local_available) {
              console.log("📁 FRONTEND DEBUG: Video available locally, using proxied access");
              setVideoUrl(`${API_ROOT}/video/${unique_id}?token=${token}`);
            }
          } else {
            console.warn("⚠️ FRONTEND DEBUG: Failed to get presigned URL, falling back to proxied access");
            setVideoUrl(`${API_ROOT}/video/${unique_id}?token=${token}`);
          }
        } catch (error) {
          console.error("❌ FRONTEND DEBUG: Error fetching presigned URL:", error);
          console.log("🔄 FRONTEND DEBUG: Falling back to proxied access");
          setVideoUrl(`${API_ROOT}/video/${unique_id}?token=${token}`);
        }
      };

      fetchPresignedUrl();
    }
  }, [unique_id, token]);

  // Handle fullscreen video state
  const handleVideoFullscreenOpen = () => {
    // Pause the background video immediately
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsVideoFullscreen(true);
  };

  const handleVideoFullscreenClose = () => {
    // Pause the modal video
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.pause();
    }
    setIsVideoFullscreen(false);
  };

  // Handle modal video load and play
  const handleModalVideoLoad = () => {
    // When modal video loads, ensure background video is paused
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Handle modal video play
  const handleModalVideoPlay = () => {
    // When modal video starts playing, ensure background video stays paused
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };


  const handleLogout = () => {
    localStorage.removeItem("token");
        navigate("/");
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error("Video loading error:", e);
    const videoElement = e.currentTarget;
    console.log("Video error details:", videoElement.error);
    console.log("🔍 FRONTEND DEBUG: Current video URL:", videoElement.src);

    // If S3 URL failed, try getting a new presigned URL
    if (videoElement.src.includes('amazonaws.com')) {
      console.log("🔍 FRONTEND DEBUG: S3 presigned URL failed, getting new presigned URL");
      
      const fetchNewPresignedUrl = async () => {
        try {
          const response = await fetch(`${API_ROOT}/video/${unique_id}/url?token=${token}&retry=${Date.now()}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.presigned_url) {
              console.log("✅ Got new presigned URL for retry");
              setVideoUrl(data.presigned_url);
              return;
            }
          }
        } catch (error) {
          console.error("Error fetching new presigned URL:", error);
        }
        
        // Fallback to proxied access
        console.log("🔄 Falling back to proxied access");
        setVideoUrl(`${API_ROOT}/video/${unique_id}?token=${token}&t=${Date.now()}`);
      };

      fetchNewPresignedUrl();
      return;
    }

    // For non-S3 URLs, retry with proxied access
    if (videoRef.current && token) {
      const newUrl = `${API_ROOT}/video/${unique_id}?token=${token}&t=${Date.now()}`;
      console.log("Retrying video load with URL:", newUrl);
      setVideoUrl(newUrl);
    }
  };

  const handleVideoLoad = () => {
    console.log("Video loaded successfully");
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  // Measure video container height to match table height
  useEffect(() => {
    const updateVideoHeight = () => {
      if (videoContainerRef.current && isCampaignMode && showSubmissionPage) {
        const height = videoContainerRef.current.offsetHeight;
        setVideoHeight(height);
      }
    };

    // Update on mount and window resize
    updateVideoHeight();
    window.addEventListener('resize', updateVideoHeight);
    
    // Also update after a short delay to ensure layout is complete
    const timeoutId = setTimeout(updateVideoHeight, 100);

    return () => {
      window.removeEventListener('resize', updateVideoHeight);
      clearTimeout(timeoutId);
    };
  }, [isCampaignMode, showSubmissionPage, videoUrl]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const handleSeek = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    
    handleSeek(newTime);
  };

  const handleVolumeChange = (newVolume: number) => {
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = volume || 0.5;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const jumpToTimestamp = (time: number) => {
    handleSeek(time);
    // Pause video when jumping to timestamp
    if (videoRef.current) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
  };

  // Submit video for campaign
  const handleSubmitVideo = async () => {
    if (!campaignUuid || !unique_id) return;
    
    // Convert creator comments to array format for backend
    const commentsArray = Object.entries(creatorComments).map(([reqNum, comment]) => ({
      requirement_number: parseInt(reqNum),
      comment: comment
    })).filter(c => c.comment && c.comment.trim().length > 0);
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ROOT}/submit-campaign-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaign_uuid: campaignUuid,
          analysis_id: unique_id,
          creator_comments: commentsArray
        })
      });
      
      if (!response.ok) {
        throw new Error('Submission failed');
      }
      
      const data = await response.json();
      setProofLink(data.proof_link || `${window.location.origin}/campaign-result/${unique_id}`);
      setSubmissionComplete(true);
    } catch (err) {
      console.error('Submission error:', err);
      alert('Failed to submit video. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Single effect to fetch analysis data - only runs when unique_id changes
  useEffect(() => {
    // Skip if no unique_id
      if (!unique_id) {
        setIsLoading(false);
        return;
      }
      
    let isCancelled = false;
    
    const fetchAnalysis = async () => {
      // Reset state for new fetch
      setIsLoading(true);
      setError(null);
      setAnalysis(null);
      
      const token = localStorage.getItem("token");
      
      // For campaign results (pathname starts with /campaign-result/), token is optional
      // For regular results, token is required
      const isCampaignResultPath = location.pathname.startsWith('/campaign-result/');
      
      if (!token && !isCampaignResultPath) {
        navigate("/");
        return;
      }
      
      try {
        console.log("Fetching analysis for:", unique_id);
        
        // Determine which endpoint to use:
        // - For campaign results without auth: use public /campaign-analysis/ endpoint
        // - For authenticated users: use regular /analysis/ endpoint
        const campaignParam = new URLSearchParams(location.search).get('campaign');
        const usePublicEndpoint = isCampaignResultPath && !token;
        
        let response;
        if (usePublicEndpoint) {
          // Public endpoint for campaign creators (no auth required)
          const url = campaignParam 
            ? `${API_ROOT}/campaign-analysis/${unique_id}?campaign_uuid=${campaignParam}`
            : `${API_ROOT}/campaign-analysis/${unique_id}`;
          console.log("📡 Using PUBLIC campaign-analysis endpoint:", url);
          response = await fetch(url);
        } else {
          // Authenticated endpoint
          const headers: HeadersInit = {};
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }
          response = await fetch(`${API_ROOT}/analysis/${unique_id}`, { headers });
        }
        
        if (isCancelled) return;
        
        if (!response.ok) throw new Error("Failed to fetch analysis");
        const data = await response.json();
        
        if (isCancelled) return;
        
        console.log("📥 Analysis received:", Object.keys(data));
        
        setAnalysis(data);
        
      } catch (err) {
        if (isCancelled) return;
        console.error("Error fetching analysis:", err);
        setError("Failed to load analysis");
      } finally {
        if (!isCancelled) {
        setIsLoading(false);
        }
      }
    };
    
    fetchAnalysis();
    
    return () => { isCancelled = true; };
  }, [unique_id, navigate, location.pathname]);

  // Ensure background video is paused when modal is open
  useEffect(() => {
    if (isVideoFullscreen && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isVideoFullscreen]);

  // When analysis loads, prefer backend-provided recommendations so they are ready immediately.
  // Only fall back to calling the recommendations endpoint if needed (e.g. older analyses).
  useEffect(() => {
    if (!analysis) return;

    // If backend already attached recommendations, use them directly
    const backendRecs = (analysis as any).recommendations;
    if (Array.isArray(backendRecs) && backendRecs.length > 0) {
      // Normalize priority values from backend
      const normalizedRecs = backendRecs.map((rec: any) => {
        if (rec.priority) {
          const priority = String(rec.priority).toLowerCase().trim();
          // Normalize variations
          if (priority === 'quick fix' || priority === 'quickfix' || priority === 'quick-fix') {
            rec.priority = 'quick_fix';
          } else if (priority === 'moderate edit' || priority === 'moderate-edit') {
            rec.priority = 'moderate';
          } else if (priority === 're-shoot' || priority === 're shoot' || priority === 'reshooting') {
            rec.priority = 'reshoot';
          }
        }
        return rec;
      });
      
      // Debug: log priority distribution
      const priorityCounts = normalizedRecs.reduce((acc: any, rec: any) => {
        const p = rec.priority || 'unknown';
        acc[p] = (acc[p] || 0) + 1;
        return acc;
      }, {});
      console.log('📊 Recommendation priorities from backend:', priorityCounts);
      
      setRecommendations(normalizedRecs);
      hasGeneratedRecommendations.current = true;
      setIsGeneratingRecommendations(false);
      return;
    }

    // Fallback: generate on the fly only if not already done
    if (
      analysis.non_compliant &&
      analysis.non_compliant.length > 0 &&
      !hasGeneratedRecommendations.current
    ) {
      const generateRecommendations = async () => {
        setIsGeneratingRecommendations(true);
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(`${API_ROOT}/generate-recommendations`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              non_compliant_items: analysis.non_compliant
            })
          });
          
          if (!response.ok) throw new Error("Failed to generate recommendations");
          
          const data = await response.json();
          const recs = data.recommendations || [];
          
          // Normalize priority values
          const normalizedRecs = recs.map((rec: any) => {
            if (rec.priority) {
              const priority = String(rec.priority).toLowerCase().trim();
              // Normalize variations
              if (priority === 'quick fix' || priority === 'quickfix' || priority === 'quick-fix') {
                rec.priority = 'quick_fix';
              } else if (priority === 'moderate edit' || priority === 'moderate-edit') {
                rec.priority = 'moderate';
              } else if (priority === 're-shoot' || priority === 're shoot' || priority === 'reshooting') {
                rec.priority = 'reshoot';
              }
            }
            return rec;
          });
          
          setRecommendations(normalizedRecs);
        } catch (err) {
          console.error("Error generating recommendations:", err);
        } finally {
          setIsGeneratingRecommendations(false);
        }
      };

      hasGeneratedRecommendations.current = true;
      generateRecommendations();
    }
  }, [analysis]);

  // IMPORTANT: All useMemo hooks MUST be before any early returns to follow Rules of Hooks
  const tableData = useMemo(() => {
    if (!analysis) return [];
    const data = getRequirementsTableData(analysis);
    return [...data].sort((a, b) => {
      if (a.compliance !== b.compliance) {
        return a.compliance === '❌' ? -1 : 1;
      }
      return 0;
    });
  }, [analysis]);

  // Assign unique numbers to all requirements (non-compliant first, then compliant)
  const numberedRequirements = useMemo(() => {
    const nonCompliant = tableData.filter(row => row.compliance === '❌');
    const compliant = tableData.filter(row => row.compliance === '✅');
    
    let number = 1;
    const numbered: Array<typeof tableData[0] & { requirementNumber: number }> = [];
    
    // Number non-compliant first
    nonCompliant.forEach(row => {
      numbered.push({ ...row, requirementNumber: number++ });
    });
    
    // Then number compliant
    compliant.forEach(row => {
      numbered.push({ ...row, requirementNumber: number++ });
    });
    
    return numbered;
  }, [tableData]);

  // Map recommendations with their requirement numbers from backend
  // The backend provides requirement_index (1-indexed) for each recommendation
  // Sort by effort level: quick_fix > moderate > reshoot
  const recommendationsWithNumbers = useMemo(() => {
    if (!recommendations.length) return [];
    
    const priorityOrder: Record<string, number> = {
      'quick_fix': 1,
      'moderate': 2,
      'reshoot': 3
    };
    
    return recommendations
      .map((rec: any) => ({
        ...rec,
        requirementNumber: rec.requirement_index || null
      }))
      .sort((a: any, b: any) => {
        const priorityA = priorityOrder[a.priority?.toLowerCase() || 'reshoot'] || 999;
        const priorityB = priorityOrder[b.priority?.toLowerCase() || 'reshoot'] || 999;
        return priorityA - priorityB;
      });
  }, [recommendations]);

  // Determine compliance score - must be before useMemos that depend on it
  const complianceScore = analysis?.compliance_score !== undefined ? analysis.compliance_score : null;

  // Filtered recommendations based on priority filter
  const filteredRecommendations = useMemo(() => {
    if (priorityFilter === 'all') return recommendationsWithNumbers;
    
    const filtered = recommendationsWithNumbers.filter(rec => {
      if (!rec.priority) return false;
      const recPriority = String(rec.priority).toLowerCase().trim();
      return recPriority === priorityFilter;
    });
    
    return filtered;
  }, [recommendationsWithNumbers, priorityFilter]);

  // Calculate projected score based on filtered recommendations (all active by default)
  // Each recommendation contributes a percentage based on priority and total requirements
  const projectedScore = useMemo(() => {
    const current = complianceScore ?? 0;
    if (!analysis || filteredRecommendations.length === 0) {
      return current;
    }
    
    const totalRequirements = numberedRequirements.length;
    if (totalRequirements === 0) return current;
    
    // Calculate how many requirements would become compliant if all filtered recommendations are fixed
    const fixedCount = filteredRecommendations.length;
    const currentCompliant = numberedRequirements.filter(r => r.compliance === '✅').length;
    const newCompliant = Math.min(currentCompliant + fixedCount, totalRequirements);
    
    return Math.round((newCompliant / totalRequirements) * 100);
  }, [complianceScore, numberedRequirements, filteredRecommendations, analysis]);

  // Calculate improvement percentage
  const improvementPercentage = useMemo(() => {
    const current = complianceScore ?? 0;
    return Math.max(0, projectedScore - current);
  }, [complianceScore, projectedScore]);

  // Calculate point value for each recommendation
  // Each recommendation fixes one non-compliant requirement, so the point value is based on total requirements
  const getRecommendationPointValue = (rec: any): number => {
    const totalRequirements = numberedRequirements.length;
    if (totalRequirements === 0) return 0;
    
    // Each fixed requirement contributes equally to the score
    // Point value = (1 / totalRequirements) * 100
    const exactValue = (1 / totalRequirements) * 100;
    
    // Round to nearest integer for display, but ensure it shows at least 1%
    return Math.max(1, Math.round(exactValue));
  };

  // "What If" simulator - Calculate potential score improvement based on filtered recommendations
  const whatIfSimulation = useMemo(() => {
    if (!complianceScore || !analysis) return null;
    
    const totalRequirements = numberedRequirements.length;
    if (totalRequirements === 0) return null;
    
    const currentCompliant = numberedRequirements.filter(r => r.compliance === '✅').length;
    // Use filtered recommendations instead of all recommendations
    const issuesCount = filteredRecommendations.length;
    
    if (issuesCount === 0) return null;
    
    // Simulate fixing filtered issues
    const potentialCompliant = Math.min(currentCompliant + issuesCount, totalRequirements);
    const potentialScore = (potentialCompliant / totalRequirements) * 100;
    
    const improvement = Math.round(potentialScore - complianceScore);
    
    // Only show if there's actual improvement
    if (improvement <= 0) return null;
    
    return {
      currentScore: Math.round(complianceScore),
      potentialScore: Math.round(potentialScore),
      improvement: improvement,
      issuesCount: issuesCount,
      priorityLabel: priorityFilter === 'all' ? 'all' : priorityFilter
    };
  }, [complianceScore, numberedRequirements, filteredRecommendations, analysis, priorityFilter]);

  // Extract only essential metadata fields for "Video Insights" modal
  const analysisMetadata = useMemo(() => {
    if (!analysis) return [];
    
    const metadata: Array<{ label: string; value: any; key: string }> = [];
    
    // Campaign Name
    if (analysis.input?.campaignName) {
      metadata.push({
        label: 'Campaign Name',
        value: analysis.input.campaignName,
        key: 'campaign_name'
      });
    }
    
    // Total Requirements
    if (analysis.total_requirements !== undefined) {
      metadata.push({
        label: 'Total Requirements',
        value: analysis.total_requirements,
        key: 'total_requirements'
      });
    }
    
    // Total Processing Time
    if (analysis.total_processing_time !== undefined) {
      metadata.push({
        label: 'Total Processing Time',
        value: `${Math.round(analysis.total_processing_time)}s`,
        key: 'total_processing_time'
      });
    }
    
    // Check Created
    if (analysis.created_at) {
      metadata.push({
        label: 'Check Created',
        value: new Date(analysis.created_at * 1000).toLocaleString(),
        key: 'created_at'
      });
    }
    
    return metadata;
  }, [analysis]);

  // Extract timeline markers from non-compliant requirements
  const timelineMarkers = useMemo(() => {
    const markers: TimelineMarker[] = [];
    
    numberedRequirements.forEach(req => {
      if (req.compliance === '❌' && req.ai_answer) {
        const timestamps = parseTimestampsFromText(req.ai_answer);
        timestamps.forEach(time => {
          markers.push({
            time,
            requirementNumber: req.requirementNumber,
            requirement: req.requirement,
            aiAnswer: req.ai_answer || ''
          });
        });
      }
    });
    
    // Sort by time
    return markers.sort((a, b) => a.time - b.time);
  }, [numberedRequirements]);

  // Tour steps for video result page
  const tourSteps = [
    {
      id: 'compliance-score',
      text: 'This compliance score shows how well the video meets your brand guidelines. It\'s calculated based on how many of your DOs and DON\'Ts requirements are met.',
      attachTo: { element: '[title="Compliance Score"]', on: 'bottom' as const },
      buttons: [
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'insights-button',
      text: 'Click this button to see detailed video insights, e.g., sensitive content detection, brand and product detection and visibility, among other things.',
      attachTo: { element: '[title="Video Insights"]', on: 'left' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'non-compliant-section',
      text: 'Non-compliant requirements are prominently displayed at the top. Each card shows what requirement was not met and the AI\'s explanation.',
      attachTo: { element: '.space-y-3', on: 'top' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Done', action: function() { (this as any).complete(); } }
      ]
    }
  ];

  const startShepherdTour = () => {
    const tour = new Shepherd.Tour({
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: true,
        classes: 'shadow-lg bg-white shepherd-custom-buttons',
      },
      useModalOverlay: true,
    });
    
    tourSteps.forEach(step => tour.addStep(step));
    tour.start();
  };

  // Don't return early for loading - handle it as overlay instead

  // Handle error or no analysis cases
  if (error || (!analysis && !isLoading)) {
    return (
      <div className="flex h-screen overflow-hidden">
        {/* CRITICAL: Don't render sidebar for campaign mode - it checks token and redirects! */}
        {!isCampaignMode && (
          sidebarOpen ? (
            <AnalysisSidebar onHideSidebar={() => setSidebarOpen(false)} />
          ) : (
            <FoldedSidebar onShowSidebar={() => setSidebarOpen(true)} />
          )
        )}
        <div 
          className="flex-1 flex items-center justify-center"
          style={{ background: PALETTE.brightSnow }}
        >
          <div className="text-center">
            <div className="text-6xl">⚠️</div>
            <div className="text-xl font-semibold mt-4" style={{ color: PALETTE.gunmetal }}>
              {error || "Analysis not found"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render content until we have analysis data
  if (!analysis) {
    console.log("🎬 Rendering loading state - isLoading:", isLoading, "analysis:", !!analysis);
    
    // For brand users viewing submissions, show a clean loading state without sidebar
    // Use isCampaignResultPath directly (synchronous) instead of isCampaignMode state
    if (isCampaignResultPath && isBrand) {
    return (
      <div className="flex h-screen overflow-hidden">
          <div 
            className="flex-1 flex items-center justify-center"
            style={{ background: PALETTE.brightSnow }}
          >
            {/* Clean loading state for brand submission view - no sidebar, no technical details */}
            {isLoading ? (
              <div className="text-center animate-fade-in">
                <div 
                  className="animate-spin rounded-full h-12 w-12 mx-auto"
                  style={{ 
                    border: `3px solid ${PALETTE.americanSilver}`,
                    borderTopColor: PALETTE.inchworm,
                  }}
                ></div>
              </div>
            ) : (
              <div className="text-center opacity-50">
                <div style={{ color: PALETTE.gunmetal }}>
                  No submission data available
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // For campaign mode (public users), show clean loading WITHOUT sidebar (sidebar redirects to auth!)
    // For regular authenticated users, show loading with sidebar
    return (
      <div className="flex h-screen overflow-hidden">
        {/* CRITICAL: Don't render sidebar for campaign mode - it checks token and redirects! */}
        {!isCampaignMode && (
          sidebarOpen ? (
          <AnalysisSidebar onHideSidebar={() => setSidebarOpen(false)} />
        ) : (
          <FoldedSidebar onShowSidebar={() => setSidebarOpen(true)} />
          )
        )}
        <div 
          className="flex-1 flex items-center justify-center"
          style={{ background: PALETTE.brightSnow }}
        >
          {/* Loading state when no analysis data */}
          {isLoading ? (
            <div className="text-center animate-fade-in">
              <div 
                className="animate-spin rounded-full h-16 w-16 mx-auto mb-6"
                style={{ 
                  border: `4px solid ${PALETTE.americanSilver}`,
                  borderTopColor: PALETTE.inchworm,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}
              ></div>
              <div 
                className="text-base font-medium opacity-80"
                style={{ color: PALETTE.gunmetal }}
              >
                Loading analysis...
              </div>
            </div>
          ) : (
            <div className="text-center opacity-50">
              <div style={{ color: PALETTE.gunmetal }}>
                No analysis data
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main render - analysis is guaranteed to exist at this point
  console.log("🎬 Rendering main content with analysis");
  console.log("🔍 Campaign mode state:", { isCampaignMode, showSubmissionPage, campaignUuid });
  console.log("📊 Requirements data:", { 
    numberedRequirements: numberedRequirements?.length, 
    videoUrl: !!videoUrl,
    videoHeight 
  });
  
  return (
    <div className="flex h-screen overflow-hidden">
      <SEO
        title="Video Analysis Results"
        description="View your BrandComply video compliance analysis. See how your content matches the campaign brief requirements."
        path={`/result/${unique_id}`}
      />
      {/* Sidebar - Hide in campaign mode */}
      {!isCampaignMode && (
        <>
      {sidebarOpen ? (
        <AnalysisSidebar onHideSidebar={() => setSidebarOpen(false)} />
      ) : (
        <FoldedSidebar onShowSidebar={() => setSidebarOpen(true)} />
          )}
        </>
      )}

      {/* Main Content */}
      <div 
        className="flex-1 flex flex-col px-8 py-4 pb-24 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden relative"
      >
        {/* Loading Overlay */}
        {isLoading && (
          <div 
            className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
            style={{ 
              background: `rgba(248, 250, 252, 0.98)`,
              backdropFilter: 'blur(8px)',
              zIndex: 9999
            }}
          >
            <div className="text-center animate-fade-in">
              <div 
                className="animate-spin rounded-full h-16 w-16 mx-auto mb-6"
                style={{ 
                  border: `4px solid ${PALETTE.americanSilver}`,
                  borderTopColor: PALETTE.inchworm,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}
              ></div>
              <div 
                className="text-base font-medium opacity-80"
                style={{ color: PALETTE.gunmetal }}
              >
                Loading analysis...
              </div>
            </div>
          </div>
        )}
        {/* Feature Request & Help/Tour Buttons - Top Right */}
        <div className="absolute top-4 right-8 z-10 flex items-center gap-3">
          <FeatureRequestButton />
          {!isCampaignMode && (
            <button
              onClick={startShepherdTour}
              aria-label="Help / Tour"
              className="shepherd-help-btn bg-white border border-gray-300 shadow-md rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition"
              style={{ boxShadow: '0 2px 8px rgba(36,56,55,0.10)' }}
            >
              <HelpCircle size={20} color="#243837" />
            </button>
          )}
        </div>

        {/* Back Button - Top Left - Hide in campaign mode */}
        {!isCampaignMode && (
        <div className="mb-4">
          <Button
              onClick={() => navigate(`${getBasePath()}/upload`)}
            variant="ghost"
            className="flex items-center gap-2"
            style={{ 
              color: PALETTE.gunmetal
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
        </div>
        )}


        {/* Campaign Mode Process Wizard Stepper - 3 Steps - Only show for public campaign links (creators), not for brand users viewing from sidebar */}
        {isCampaignMode && campaignQueryParam && (
          <div className="mb-8 w-full flex justify-center">
            <div className="flex items-center justify-center gap-4 w-full max-w-4xl">
              {/* Step 1: Video Upload */}
              <div className="flex items-center flex-1 justify-center">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                  style={{
                    background: PALETTE.inchworm,
                    color: PALETTE.gunmetal,
                  }}
                >
                  1
                </div>
                <span
                  className="ml-2 text-sm font-medium whitespace-nowrap hidden sm:inline"
                  style={{ color: PALETTE.gunmetal }}
                >
                  Video Upload
                </span>
                <div
                  className="h-0.5 mx-2 sm:mx-4 flex-1"
                  style={{
                    minWidth: 20,
                    background: PALETTE.inchworm,
                  }}
                />
              </div>
              
              {/* Step 2: Compliance Check */}
              <div className="flex items-center flex-1 justify-center">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                  style={{
                    background: !showSubmissionPage ? PALETTE.gunmetal : PALETTE.inchworm,
                    color: !showSubmissionPage ? PALETTE.brightSnow : PALETTE.gunmetal,
                  }}
                >
                  2
                </div>
                <span
                  className="ml-2 text-sm font-medium whitespace-nowrap hidden sm:inline"
                  style={{ color: !showSubmissionPage ? PALETTE.gunmetal : PALETTE.americanSilver }}
                >
                  Compliance Check
                </span>
                <div
                  className="h-0.5 mx-2 sm:mx-4 flex-1"
                  style={{
                    minWidth: 20,
                    background: showSubmissionPage ? PALETTE.inchworm : PALETTE.americanSilver,
                  }}
                />
              </div>
              
              {/* Step 3: Submission */}
              <div className="flex items-center flex-1 justify-center">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                  style={{
                    background: showSubmissionPage ? PALETTE.gunmetal : PALETTE.americanSilver,
                    color: showSubmissionPage ? PALETTE.brightSnow : PALETTE.gunmetal,
                  }}
                >
                  3
                </div>
                <span
                  className="ml-2 text-sm font-medium whitespace-nowrap hidden sm:inline"
                  style={{ color: showSubmissionPage ? PALETTE.gunmetal : PALETTE.americanSilver }}
                >
                  Submission
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Creator Mode Process Wizard Stepper - Hide in campaign mode */}
        {!isCampaignMode && (
        <div className="mb-8 w-full flex justify-center">
          <div className="flex items-center justify-center space-x-2 md:space-x-4 max-w-4xl w-full">
          {STEPS.map((label, idx) => (
            <React.Fragment key={label}>
                <div className="flex items-center space-x-2">
                <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 cursor-pointer transition-all hover:opacity-80"
                  style={{
                      background: idx === 3 ? PALETTE.gunmetal : idx < 3 ? PALETTE.inchworm : PALETTE.americanSilver,
                      color: idx === 3 ? PALETTE.brightSnow : PALETTE.gunmetal,
                  }}
                  onClick={() => {
                    if (idx === 0) {
                      navigate('/upload');
                    } else if (idx === 1) {
                        navigate('/upload');
                    } else if (idx === 2) {
                        navigate('/upload');
                      }
                  }}
                  title={`Go to ${label}`}
                >
                  {idx + 1}
                </div>
                <span
                    className="text-xs md:text-sm font-semibold hidden sm:inline cursor-pointer transition-all hover:opacity-80"
                    style={{ 
                      color: idx === 3 ? PALETTE.gunmetal : PALETTE.americanSilver,
                      maxWidth: '100px',
                      lineHeight: '1.3',
                      display: 'inline-block',
                      textAlign: 'left'
                    }}
                  onClick={() => {
                    if (idx === 0) {
                      navigate('/upload');
                    } else if (idx === 1) {
                        navigate('/upload');
                    } else if (idx === 2) {
                        navigate('/upload');
                      }
                  }}
                  title={`Go to ${label}`}
                >
                  {label}
                </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className="w-8 md:w-12 h-0.5 flex-shrink-0" 
                    style={{
                      background: idx < 3 ? PALETTE.inchworm : PALETTE.americanSilver
                    }}
                  />
                )}
            </React.Fragment>
          ))}
          </div>
        </div>
        )}

        {/* Header - Remove completely */}

        <div className="flex-grow flex flex-col justify-start pt-4 px-6 overflow-y-auto">
          {/* Submission Page - Campaign Mode */}
          {(() => {
            console.log("🎯 RENDER CHECK:", { 
              isCampaignMode, 
              showSubmissionPage, 
              hasAnalysis: !!analysis,
              numberedRequirementsCount: numberedRequirements?.length || 0,
              videoUrl: !!videoUrl,
              videoHeight,
              pathname: location.pathname
            });
            return null;
          })()}
          
          {isCampaignMode && showSubmissionPage && (
            <div className="w-full max-w-7xl mx-auto mb-24">
              {/* Two Column Layout: Video Player (Left) and Requirements Table (Right) */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
                {/* Left Column: Video Player - 25% width with 9:16 aspect ratio */}
                <div className="w-full lg:col-span-1">
                  {videoUrl && (
                    <Card className="border-0 shadow-lg w-full">
                      <CardContent className="p-0">
                        <div 
                          ref={videoContainerRef}
                          className="relative w-full rounded-lg overflow-hidden" 
                          style={{ 
                            aspectRatio: '9/16',
                            maxWidth: '100%'
                          }}
                        >
                          {/* Compliance Score Overlay */}
                          <ComplianceScoreOverlay currentScore={complianceScore} />
                          
                          {/* Submission Information Overlays - Below compliance score - Hide when playing */}
                          {!isVideoPlaying && (
                          <div 
                            className="absolute top-32 left-4 right-4 z-20 space-y-2"
                          >
                            {analysis?.input?.campaignName && (
                              <div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(8px)',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <div className="text-sm">
                                  <span className="font-semibold" style={{ color: `${PALETTE.gunmetal}80` }}>Campaign: </span>
                                  <span style={{ color: PALETTE.gunmetal }}>{analysis.input.campaignName}</span>
                                </div>
                              </div>
                            )}
                            
                            {(analysis as any)?.creator_name && (
                              <div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(8px)',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <div className="text-sm">
                                  <span className="font-semibold" style={{ color: `${PALETTE.gunmetal}80` }}>Creator: </span>
                                  <span style={{ color: PALETTE.gunmetal }}>{(analysis as any).creator_name}</span>
                                </div>
                              </div>
                            )}
                            
                            {analysis?.created_at && (
                              <div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(8px)',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <div className="text-sm">
                                  <span className="font-semibold" style={{ color: `${PALETTE.gunmetal}80` }}>Timestamp: </span>
                                  <span style={{ color: PALETTE.gunmetal }}>
                                    {(() => {
                                      try {
                                        let date: Date;
                                        if (typeof analysis.created_at === 'number') {
                                          // Unix timestamp (seconds)
                                          date = new Date(analysis.created_at * 1000);
                                        } else if (typeof analysis.created_at === 'string' && !analysis.created_at.includes('Z') && !analysis.created_at.includes('+')) {
                                          // SQLite UTC timestamp without timezone indicator
                                          const utcTimestamp = analysis.created_at.replace(' ', 'T') + 'Z';
                                          date = new Date(utcTimestamp);
                                        } else {
                                          date = new Date(analysis.created_at);
                                        }
                                        
                                        if (isNaN(date.getTime())) return 'N/A';
                                        
                                        return date.toLocaleString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        });
                                      } catch (e) {
                                        return 'N/A';
                                      }
                                    })()}
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            {unique_id && (
                              <div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(8px)',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <div className="text-sm">
                                  <span className="font-semibold" style={{ color: `${PALETTE.gunmetal}80` }}>Analysis ID: </span>
                                  <span className="font-mono text-xs" style={{ color: PALETTE.gunmetal }}>{unique_id}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          )}
                          
                          {/* Video Element */}
                          <video
                            key={videoUrl}
                            ref={videoRef}
                            src={videoUrl}
                            className="w-full h-full"
                            style={{ objectFit: 'cover' }}
                            playsInline
                            preload="metadata"
                            onError={handleVideoError}
                            onLoadedMetadata={handleLoadedMetadata}
                            onTimeUpdate={handleTimeUpdate}
                            onPlay={handleVideoPlay}
                            onPause={handleVideoPause}
                            onEnded={handleVideoEnded}
                            controls
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Right Column: Requirements Table - 75% width, matching video height */}
                <div className="w-full lg:col-span-3 flex flex-col">
                  {(() => {
                    console.log("AGGRID RENDER CHECK", {
                      isCampaignMode,
                      showSubmissionPage,
                      numberedRequirementsLength: numberedRequirements?.length || 0,
                      videoHeight
                    });
                    return null;
                  })()}
                  
                  {/* AG Grid Table - Height matches video player exactly */}
                  {numberedRequirements && numberedRequirements.length > 0 ? (
                  <div 
                    className="ag-theme-quartz requirements-table" 
                    style={{ 
                      width: '100%',
                      height: videoHeight ? `${videoHeight}px` : '600px'
                    }}
                  >
                    <AgGridReact
                      rowData={numberedRequirements}
                      pagination={false}
                      rowHeight={40}
                      suppressPaginationPanel={true}
                      domLayout="normal"
                      onGridReady={(params) => {
                        console.log("AGGRID onGridReady", {
                          rowCount: params.api.getDisplayedRowCount(),
                          cols: params.columnApi.getColumns()?.map((c: any) => c.getColId())
                        });
                      }}
                      columnDefs={[
                        {
                          headerName: 'Compliance',
                          field: 'compliance',
                          width: 100,
                          resizable: false,
                          filter: false,
                          cellStyle: { 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const isCompliant = params.value === '✅';
                            return (
                              <div 
                                className="flex items-center justify-center h-full w-full cursor-help"
                                style={{ margin: '0 auto' }}
                              >
                                <div
                                  className="flex items-center justify-center rounded-full"
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    backgroundColor: isCompliant ? PALETTE.inchworm : '#f43f5e'
                                  }}
                                >
                                  {isCompliant ? (
                                    <Check className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                                  ) : (
                                    <X className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                                  )}
                                </div>
                              </div>
                            );
                          },
                          tooltipValueGetter: (params: any) => {
                            const aiAnswer = params.data?.ai_answer;
                            if (!aiAnswer) return null;
                            
                            const isCompliant = params.data?.compliance === '✅';
                            const status = isCompliant ? 'Compliant' : 'Non-Compliant';
                            return `${status}: ${aiAnswer}`;
                          }
                        },
                        {
                          headerName: 'Requirement',
                          field: 'requirement',
                          flex: 1,
                          resizable: false,
                          filter: false,
                          wrapText: false,
                          autoHeight: false,
                          cellStyle: { 
                            lineHeight: '1.4', 
                            paddingTop: '8px', 
                            paddingBottom: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const requirement = params.value || '';
                            return (
                              <div 
                                className="w-full truncate"
                                title={requirement}
                                style={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: PALETTE.gunmetal
                                }}
                              >
                                {requirement}
                              </div>
                            );
                          },
                          tooltipValueGetter: (params: any) => {
                            return params.value || '';
                          },
                          tooltipField: 'requirement'
                        },
                        {
                          headerName: 'Comment',
                          width: 200,
                          resizable: false,
                          filter: false,
                          editable: !submissionComplete && localStorage.getItem("userType") !== 'brand',
                          cellEditor: 'agTextCellEditor',
                          cellEditorParams: {
                            maxLength: 500
                          },
                          cellStyle: { lineHeight: '1.4', paddingTop: '8px', paddingBottom: '8px' },
                          valueGetter: (params: any) => {
                            return creatorComments[params.data?.requirementNumber] || '';
                          },
                          valueSetter: (params: any) => {
                            if (!params.data || submissionComplete || localStorage.getItem("userType") === 'brand') return false;
                            const newComments = { ...creatorComments };
                            newComments[params.data.requirementNumber] = params.newValue;
                            setCreatorComments(newComments);
                            return true;
                          },
                          cellRenderer: (params: ICellRendererParams) => {
                            const comment = creatorComments[params.data?.requirementNumber || 0] || '';
                            const isReadOnly = submissionComplete || localStorage.getItem("userType") === 'brand';
                            return (
                              <div className="flex items-center h-full">
                                <span 
                                  className="text-sm" 
                                  style={{ 
                                    color: comment ? PALETTE.gunmetal : `${PALETTE.gunmetal}40`,
                                    fontStyle: !comment && isReadOnly ? 'italic' : 'normal'
                                  }}
                                >
                                  {comment || (isReadOnly ? 'No comment provided' : 'Click to add comment...')}
                                </span>
                              </div>
                            );
                          }
                        }
                      ]}
                      defaultColDef={{
                        sortable: true,
                        filter: false,
                        resizable: false
                      }}
                      tooltipShowDelay={300}
                      tooltipHideDelay={2000}
                      enableBrowserTooltips={true}
                    />
                  </div>
                  ) : (
                    <div 
                      className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
                      style={{ 
                        height: videoHeight ? `${videoHeight}px` : '600px',
                        minHeight: '400px'
                      }}
                    >
                      <div className="text-center p-8">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: PALETTE.americanSilver }} />
                        <p className="text-lg font-medium" style={{ color: PALETTE.gunmetal }}>
                          No requirements data available
                        </p>
                        <p className="text-sm mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                          The analysis results could not be loaded.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Disclaimer */}
                  <p 
                    className="mt-2 text-xs text-center"
                    style={{ 
                      color: `${PALETTE.gunmetal}60`,
                      fontSize: '10px'
                    }}
                  >
                    BrandComply can make mistakes. Please verify important information.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submission Information Modal */}
          {showSubmissionInfoModal && (
            <div 
              className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4"
              onClick={() => setShowSubmissionInfoModal(false)}
            >
              <div 
                className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold" style={{ color: PALETTE.gunmetal }}>
                    Submission Information
                  </h2>
                  <Button
                    onClick={() => setShowSubmissionInfoModal(false)}
                    variant="ghost"
                    size="sm"
                    className="p-1"
                  >
                    <X className="w-5 h-5" style={{ color: PALETTE.gunmetal }} />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-1" style={{ color: `${PALETTE.gunmetal}70` }}>Campaign:</p>
                    <p className="text-base font-semibold" style={{ color: PALETTE.gunmetal }}>
                      {analysis?.input?.campaignName || 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium mb-1" style={{ color: `${PALETTE.gunmetal}70` }}>Compliance Score:</p>
                    <p className="text-2xl font-bold" style={{ color: complianceScore && complianceScore >= 70 ? PALETTE.inchworm : PALETTE.orange }}>
                      {complianceScore !== null ? `${Math.round(complianceScore)}%` : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium mb-1" style={{ color: `${PALETTE.gunmetal}70` }}>Total Requirements:</p>
                    <p className="text-base font-semibold" style={{ color: PALETTE.gunmetal }}>
                      {numberedRequirements.length}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium mb-1" style={{ color: `${PALETTE.gunmetal}70` }}>Video Analysis ID:</p>
                    <p className="font-mono text-xs break-all" style={{ color: `${PALETTE.gunmetal}70` }}>
                      {unique_id}
                    </p>
                  </div>
                  
                  {analysis?.created_at && (
                    <div>
                      <p className="text-sm font-medium mb-1" style={{ color: `${PALETTE.gunmetal}70` }}>Analysis Timestamp:</p>
                      <p className="text-sm" style={{ color: PALETTE.gunmetal }}>
                        {new Date(analysis.created_at * 1000).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          timeZoneName: 'short'
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Video and Recommendations Side by Side - 40/60 split - Hide when on submission page */}
          {(!isCampaignMode || !showSubmissionPage) && (
          <div className="w-full max-w-7xl mx-auto mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Video Section - Left (2 columns = 40%, scaled down 10%) */}
              <div className="lg:col-span-2 w-full flex justify-center">
                {videoUrl && (
                  <div className="p-0 rounded-lg overflow-hidden" style={{ width: '90%' }}>
                    <div className="relative w-full aspect-[9/16]">
                      {/* Compliance Score Overlay */}
                      <ComplianceScoreOverlay currentScore={complianceScore} />
                      
                      {/* Video Element - No native controls */}
                      <video
                        key={videoUrl}
                        ref={videoRef}
                        src={videoUrl}
                        className="w-full h-full rounded-lg"
                        style={{ objectFit: 'cover' }}
                        playsInline
                        preload="metadata"
                        onError={handleVideoError}
                        onLoadedMetadata={handleLoadedMetadata}
                        onTimeUpdate={handleTimeUpdate}
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                        onEnded={handleVideoEnded}
                      >
                        Your browser does not support the video tag.
                      </video>

                      {/* Custom Video Controls Overlay - Positioned at bottom */}
                      <div 
                        className="absolute bottom-0 left-0 right-0 p-4 video-controls-overlay"
                        style={{ 
                          background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 50%, transparent 100%)',
                          backdropFilter: 'blur(4px)'
                        }}
                      >
                        {/* Timeline with Markers */}
                        <div className="mb-3">
                          <div 
                            className="relative h-2 bg-gray-300 bg-opacity-50 rounded-full cursor-pointer group"
                            onClick={handleTimelineClick}
                          >
                            {/* Progress Bar */}
                            <div 
                              className="absolute top-0 left-0 h-full rounded-full transition-all"
                              style={{ 
                                width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                                background: PALETTE.inchworm
                              }}
                            />
                            
                            {/* Timeline Markers for Non-Compliant Issues */}
                            {timelineMarkers.map((marker, idx) => {
                              const position = duration > 0 ? (marker.time / duration) * 100 : 0;
                              return (
                                <div
                                  key={idx}
                                  className="absolute top-1/2 -translate-y-1/2 cursor-pointer z-10 hover:scale-125 transition-transform"
                                  style={{ left: `${position}%` }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    jumpToTimestamp(marker.time);
                                  }}
                                  title={`#${marker.requirementNumber} at ${formatTime(marker.time)}`}
                                >
                                  <div 
                                    className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shadow-lg"
                                    style={{ 
                                      background: PALETTE.paleViolet,
                                      color: PALETTE.brightSnow,
                                      border: '2px solid white'
                                    }}
                                  >
                                    {marker.requirementNumber}
                                  </div>
                                </div>
                              );
                            })}
                            
                            {/* Playhead */}
                            <div 
                              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white border-2 shadow-lg"
                              style={{ 
                                left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                                borderColor: PALETTE.gunmetal,
                                marginLeft: '-6px'
                              }}
                            />
                          </div>
                          
                          {/* Time Display */}
                          <div className="flex justify-between mt-1 text-xs text-white">
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                          </div>
                        </div>

                        {/* Control Buttons */}
                        <div className="flex items-center gap-3">
                          {/* Play/Pause */}
                          <button
                            onClick={togglePlayPause}
                            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-20 transition-colors"
                            style={{ color: PALETTE.brightSnow }}
                          >
                            {isVideoPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                          </button>

                          {/* Volume Control */}
                          <div className="flex items-center gap-2 flex-1">
                            <button
                              onClick={toggleMute}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-20 transition-colors"
                              style={{ color: PALETTE.brightSnow }}
                            >
                              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                            </button>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={isMuted ? 0 : volume}
                              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                              className="w-20 h-1 rounded-lg cursor-pointer"
                              style={{
                                background: `linear-gradient(to right, ${PALETTE.inchworm} 0%, ${PALETTE.inchworm} ${(isMuted ? 0 : volume) * 100}%, rgba(255, 255, 255, 0.3) ${(isMuted ? 0 : volume) * 100}%, rgba(255, 255, 255, 0.3) 100%)`
                              }}
                            />
                          </div>

                          {/* Video Insights Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowAnalysisModal(true);
                            }}
                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-20 transition-colors"
                            style={{ color: PALETTE.brightSnow }}
                            title="Video Insights"
                          >
                            <Info className="w-5 h-5" />
                          </button>

                          {/* Fullscreen */}
                          <button
                            onClick={handleVideoFullscreenOpen}
                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-20 transition-colors"
                            style={{ color: PALETTE.brightSnow }}
                            title="Fullscreen"
                          >
                            <Maximize className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Suggestions Section - Right (3 columns = 60%) */}
              <div className="lg:col-span-3 w-full">
                {numberedRequirements.filter(row => row.compliance === '❌').length > 0 && (
                  <Card className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200/50">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-medium uppercase tracking-wide" style={{ color: `${PALETTE.gunmetal}70` }}>
                          SUGGESTIONS
                          </h2>
                        <span className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                          {filteredRecommendations.length} {filteredRecommendations.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                      
                      {/* Filters */}
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => setPriorityFilter('all')}
                          className="px-3 py-1.5 rounded text-sm font-semibold transition-all"
                          style={{
                            background: priorityFilter === 'all' ? PALETTE.paleViolet : 'transparent',
                            color: priorityFilter === 'all' ? PALETTE.brightSnow : PALETTE.gunmetal,
                            border: `1px solid ${priorityFilter === 'all' ? PALETTE.paleViolet : PALETTE.americanSilver}`
                          }}
                        >
                          All
                        </button>
                        <button
                          onClick={() => setPriorityFilter('quick_fix')}
                          className="px-3 py-1.5 rounded text-sm font-semibold transition-all"
                          style={{
                            background: priorityFilter === 'quick_fix' ? PALETTE.inchworm : 'transparent',
                            color: priorityFilter === 'quick_fix' ? PALETTE.gunmetal : PALETTE.gunmetal,
                            border: `1px solid ${priorityFilter === 'quick_fix' ? PALETTE.inchworm : PALETTE.americanSilver}`
                          }}
                        >
                          Quick Fixes
                        </button>
                        <button
                          onClick={() => setPriorityFilter('moderate')}
                          className="px-3 py-1.5 rounded text-sm font-semibold transition-all"
                          style={{
                            background: priorityFilter === 'moderate' ? '#fbbf24' : 'transparent',
                            color: priorityFilter === 'moderate' ? PALETTE.brightSnow : PALETTE.gunmetal,
                            border: `1px solid ${priorityFilter === 'moderate' ? '#fbbf24' : PALETTE.americanSilver}`
                          }}
                        >
                          Moderate Edits
                        </button>
                        <button
                          onClick={() => setPriorityFilter('reshoot')}
                          className="px-3 py-1.5 rounded text-sm font-semibold transition-all"
                          style={{
                            background: priorityFilter === 'reshoot' ? '#f43f5e' : 'transparent',
                            color: priorityFilter === 'reshoot' ? PALETTE.brightSnow : PALETTE.gunmetal,
                            border: `1px solid ${priorityFilter === 'reshoot' ? '#f43f5e' : PALETTE.americanSilver}`
                          }}
                        >
                          Reshoots
                        </button>
                      </div>
                    </div>
                    
                    {/* Loading State */}
                    {isGeneratingRecommendations && (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" style={{ color: PALETTE.paleViolet }} />
                          <p className="text-sm" style={{ color: PALETTE.gunmetal }}>
                            Analyzing requirements and generating personalized recommendations...
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Issue Cards - Redesigned */}
                    {filteredRecommendations.length > 0 ? (
                      <div className="space-y-3">
                        {(recommendationsExpanded ? filteredRecommendations : filteredRecommendations.slice(0, 3)).map((rec, idx) => {
                          const pointValue = getRecommendationPointValue(rec);
                          const marker = timelineMarkers.find(m => m.requirementNumber === rec.requirementNumber);
                          const hasTimestamp = !!marker;
                          
                          // Map severity to icons and colors
                          const getSeverityConfig = (priority: string) => {
                            switch (priority) {
                              case 'quick_fix':
                                return {
                                  icon: CheckCircle2,
                                  label: 'Quick fix',
                                  bgColor: `${PALETTE.inchworm}10`,
                                  textColor: PALETTE.inchworm,
                                  borderColor: PALETTE.inchworm
                                };
                              case 'moderate':
                                return {
                                  icon: AlertTriangle,
                                  label: 'Moderate',
                                  bgColor: '#fef3c7',
                                  textColor: '#fbbf24',
                                  borderColor: '#fbbf24'
                                };
                              default: // reshoot
                                return {
                                  icon: AlertCircle,
                                  label: 'Major',
                                  bgColor: '#fee2e2',
                                  textColor: '#f43f5e',
                                  borderColor: '#f43f5e'
                                };
                            }
                          };
                          
                          const config = getSeverityConfig(rec.priority || 'reshoot');
                          const Icon = config.icon;
                          
                          return (
                            <div
                              key={idx}
                              className="group flex items-start gap-4 p-4 rounded-lg bg-white border hover:shadow-sm transition-all duration-200 cursor-pointer"
                              style={{
                                borderColor: `${PALETTE.americanSilver}50`,
                                opacity: 0,
                                animation: `fade-in 0.5s ease-out forwards`,
                                animationDelay: `${idx * 100}ms`
                              }}
                              onClick={() => {
                                if (hasTimestamp) {
                                  jumpToTimestamp(marker.time);
                                }
                              }}
                            >
                              {/* Severity Icon */}
                              <div 
                                className="p-2 rounded-lg flex-shrink-0"
                                style={{ 
                                  background: config.bgColor,
                                  color: config.textColor
                                }}
                              >
                                <Icon className="w-4 h-4" />
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    {/* Requirement Number */}
                                    {rec.requirementNumber && (
                                      <div 
                                      className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0"
                                        style={{ 
                                        background: config.bgColor,
                                        color: config.textColor
                                        }}
                                      >
                                        {rec.requirementNumber}
                                      </div>
                                    )}
                                    <span
                                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                                      style={{
                                      background: config.bgColor,
                                      color: config.textColor
                                      }}
                                    >
                                    {config.label}
                                    </span>
                                    {hasTimestamp && (
                                    <div 
                                      className="flex items-center gap-1 text-xs"
                                      style={{ color: `${PALETTE.gunmetal}70` }}
                                      >
                                      <Clock className="w-3 h-3" />
                                      {formatTime(marker.time)}
                                    </div>
                                    )}
                                  </div>
                                  <p 
                                  className="text-sm leading-relaxed"
                                  style={{ color: PALETTE.gunmetal }}
                                  >
                                    {rec.recommendation}
                                  </p>
                                </div>
                                
                              {/* Impact & Info */}
                              <div className="text-right shrink-0 flex flex-col items-end gap-2">
                                    <span 
                                  className="text-sm font-medium"
                                  style={{ color: PALETTE.inchworm }}
                                    >
                                  +{pointValue}%
                                    </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedRecommendation(rec);
                                    }}
                                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                                    style={{ color: PALETTE.paleViolet }}
                                  title="View details"
                                  >
                                    <Info className="w-4 h-4" />
                                  </button>
                              </div>
                            </div>
                          );
                        })}

                        {/* Show More/Show Less Button */}
                        {filteredRecommendations.length > 3 && (
                          <button
                            onClick={() => setRecommendationsExpanded(!recommendationsExpanded)}
                            className="w-full py-2 text-sm font-semibold rounded-lg transition-all hover:bg-gray-50 mt-2"
                            style={{ 
                              color: PALETTE.paleViolet
                            }}
                          >
                            {recommendationsExpanded ? '▲ Show Less' : `▼ Show ${filteredRecommendations.length - 3} More`}
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-center py-4" style={{ color: `${PALETTE.gunmetal}60` }}>
                        {priorityFilter !== 'all' 
                          ? `No ${priorityFilter === 'quick_fix' ? 'quick fix' : priorityFilter === 'moderate' ? 'moderate edit' : 'reshoot'} recommendations available`
                          : 'No recommendations available'}
                      </p>
                    )}
                    
                    {/* Disclaimer */}
                    <p 
                      className="mt-4 text-xs text-center"
                      style={{ 
                        color: `${PALETTE.gunmetal}60`,
                        fontSize: '10px'
                      }}
                    >
                      BrandComply can make mistakes. Please verify important information.
                    </p>
                  </Card>
                )}
              </div>
            </div>
          </div>
          )}

        </div>

        {/* Fullscreen Video Modal */}
        {isVideoFullscreen && videoUrl && (
          <div 
            className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4 fullscreen-modal"
            onClick={handleVideoFullscreenClose}
          >
            <div 
              className="relative max-w-full max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                ref={fullscreenVideoRef}
                src={videoUrl}
                controls
                className="rounded-lg aspect-[9/16]"
                style={{ 
                  objectFit: 'contain',
                  maxHeight: '90vh',
                  maxWidth: '90vw',
                  width: 'auto',
                  height: 'auto'
                }}
                playsInline
                preload="metadata"
                onLoadStart={handleModalVideoLoad}
                onLoadedData={handleModalVideoLoad}
                onPlay={handleModalVideoPlay}
                onPlaying={handleModalVideoPlay}
              >
                Your browser does not support the video tag.
              </video>
              <Button
                onClick={handleVideoFullscreenClose}
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 text-white hover:bg-opacity-70"
              >
                <X size={20} />
              </Button>
            </div>
          </div>
        )}

        {/* Recommendation Details Modal */}
        {selectedRecommendation && (
          <div 
            className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={() => setSelectedRecommendation(null)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-2xl w-full"
              style={{ background: PALETTE.brightSnow }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                    style={{ 
                      background: `${PALETTE.paleViolet}20`,
                      color: PALETTE.paleViolet
                    }}
                  >
                    {selectedRecommendation.requirementNumber || '?'}
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: PALETTE.gunmetal }}>
                    Requirement Details
                  </h2>
                </div>
                <Button
                  onClick={() => setSelectedRecommendation(null)}
                  variant="ghost"
                  size="sm"
                  className="p-1"
                  style={{ color: PALETTE.gunmetal }}
                >
                  <X size={20} />
                </Button>
              </div>
              
              <div className="space-y-4">
                {/* Original Requirement */}
                <div>
                  <h3 className="text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                    Original Requirement
                  </h3>
                  <div 
                    className="p-3 rounded-lg text-sm"
                    style={{ 
                      background: '#fee2e2',
                      color: PALETTE.gunmetal,
                      border: '1px solid #f43f5e'
                    }}
                  >
                    {(() => {
                      const requirement = numberedRequirements.find(
                        req => req.requirementNumber === selectedRecommendation.requirementNumber
                      );
                      return requirement?.requirement || selectedRecommendation.requirement || 'N/A';
                    })()}
                  </div>
                </div>

                {/* AI Analysis / Feedback */}
                <div>
                  <h3 className="text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                    Why This Was Not Met
                  </h3>
                  <div 
                    className="p-3 rounded-lg text-sm leading-relaxed"
                    style={{ 
                      background: '#f3f4f6',
                      color: PALETTE.gunmetal,
                      border: `1px solid ${PALETTE.americanSilver}`
                    }}
                  >
                    {(() => {
                      const requirement = numberedRequirements.find(
                        req => req.requirementNumber === selectedRecommendation.requirementNumber
                      );
                      return requirement?.ai_answer || 'No detailed feedback available.';
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Results Modal */}
        {showAnalysisModal && (
          <div 
            className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={() => setShowAnalysisModal(false)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto"
              style={{ background: PALETTE.brightSnow }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold" style={{ color: PALETTE.gunmetal }}>
                  Video Insights
                </h2>
                <Button
                  onClick={() => setShowAnalysisModal(false)}
                  variant="ghost"
                  size="sm"
                  className="p-1"
                  style={{ color: PALETTE.gunmetal }}
                >
                  <X size={16} />
                </Button>
              </div>
              
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {analysisMetadata.length > 0 ? (
                  analysisMetadata.map((item) => {
                        let displayValue: React.ReactNode = item.value;
                    
                    // Format boolean values
                        if (typeof item.value === 'boolean') {
                      const bgColor = item.value ? '#f0fdf4' : '#fef2f2';
                      const iconColor = item.value ? '#22c55e' : '#ef4444';
                      const textColor = item.value ? '#15803d' : '#b91c1c';

                          return (
                        <div key={item.key} className="flex justify-between items-center p-3 rounded" style={{ background: bgColor, border: `1px solid ${PALETTE.americanSilver}` }}>
                          <span className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>{item.label}</span>
                          <div className="flex items-center gap-2">
                            {item.value
                                  ? <Check className="w-4 h-4" style={{ color: iconColor }} />
                                  : <X className="w-4 h-4" style={{ color: iconColor }} />}
                            <span className="text-sm font-semibold" style={{ color: textColor }}>
                              {item.value ? 'Yes' : 'No'}
                            </span>
                              </div>
                            </div>
                          );
                        }

                    // Format array values
                    if (Array.isArray(item.value)) {
                      displayValue = item.value.length > 0 ? item.value.join(', ') : 'None';
                    }
                    
                    // Format numeric values with decimals
                    if (typeof item.value === 'number' && !Number.isInteger(item.value)) {
                      displayValue = item.value.toFixed(2);
                        }
                        
                        return (
                      <div key={item.key} className="flex justify-between items-center p-3 rounded" style={{ background: PALETTE.brightSnow, border: `1px solid ${PALETTE.americanSilver}` }}>
                        <span className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>{item.label}</span>
                        <span className="text-sm" style={{ color: PALETTE.gunmetal }}>{displayValue}</span>
                          </div>
                        );
                  })
                ) : (
                  <div className="text-center py-8 text-sm" style={{ color: '#9ca3af' }}>
                    No additional insights available
                    </div>
                )}
              </div>
            </div>
          </div>
        )}



        {/* Bottom Navigation */}
        <div 
          className="fixed bottom-8 z-20" 
          style={{ 
            left: isCampaignMode ? '0' : (sidebarOpen ? '320px' : '56px'), 
            right: '0' 
          }}
        >
          <div className="flex items-center justify-center gap-4">
            {/* Campaign mode buttons - ONLY for creators (with ?campaign= param), NOT for brands */}
            {(() => {
              const userType = localStorage.getItem("userType");
              const isBrandUser = userType === 'brand';
              const hasCampaignParam = new URLSearchParams(location.search).get('campaign') !== null;
              // Show buttons ONLY if: campaign mode + has ?campaign= param + NOT a brand + not submitted
              const showCreatorButtons = isCampaignMode && hasCampaignParam && !isBrandUser && !submissionComplete;
              
              if (showCreatorButtons) {
                return (
                  <>
                    {/* Re-Upload Button */}
                    <Button
                      onClick={() => {
                        if (campaignUuid) {
                          navigate(`/upload/${campaignUuid}`);
                        }
                      }}
                      variant="outline"
                      className="rounded-lg px-6 h-12 flex items-center justify-center gap-2"
                      style={{ 
                        background: PALETTE.brightSnow,
                        color: PALETTE.gunmetal,
                        border: `1px solid ${PALETTE.americanSilver}`
                      }}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="text-base font-semibold">Re-Upload Video</span>
                    </Button>
                    
                    {/* Final Submit Button */}
                    <Button
                      onClick={handleSubmitVideo}
                      disabled={isSubmitting}
                      className="rounded-lg px-6 h-12 flex items-center justify-center gap-2"
                      style={{ 
                        background: isSubmitting ? PALETTE.americanSilver : PALETTE.inchworm,
                        color: PALETTE.gunmetal,
                        border: 'none'
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          <span className="text-base font-semibold">Submitting...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-base font-semibold">Final Submit to Brand</span>
                        </>
                      )}
                    </Button>
                  </>
                );
              }
              
              // Show "Submitted" message for creators who already submitted
              if (isCampaignMode && hasCampaignParam && !isBrandUser && submissionComplete) {
                return (
                  <div 
                    className="px-6 py-3 rounded-lg text-base font-semibold"
                    style={{ background: `${PALETTE.inchworm}20`, color: PALETTE.gunmetal }}
                  >
                    ✅ Video submitted successfully to brand!
                  </div>
                );
              }
              
              return null;
            })()}
            
            {/* Non-campaign mode buttons (authenticated users) */}
            {!isCampaignMode && (
              <>
            <Button
              onClick={() => {
                    navigate(`${getBasePath()}/upload`);
              }}
              variant="outline"
              className="rounded-lg px-6 h-12 flex items-center justify-center gap-2"
              style={{ 
                background: PALETTE.brightSnow,
                color: PALETTE.gunmetal,
                border: `1px solid ${PALETTE.americanSilver}`
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-base font-semibold">New Analysis</span>
            </Button>
            
            {analysis && analysis.input && analysis.input.briefing_data && (
              <Button
                onClick={() => {
                  // Store the briefing data for reuse
                  localStorage.setItem('reuseBriefingData', JSON.stringify(analysis.input.briefing_data));
                  localStorage.setItem('reuseBriefingTimestamp', Date.now().toString());
                      navigate(`${getBasePath()}/upload`);
                }}
                className="rounded-lg px-6 h-12 flex items-center justify-center gap-2"
                style={{ 
                  background: PALETTE.inchworm,
                  color: PALETTE.gunmetal,
                  border: 'none'
                }}
              >
                <FilePenLine className="w-4 h-4" />
                <span className="text-base font-semibold">Reuse This Briefing</span>
              </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .truncate-cell .ag-cell-value {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: 100% !important;
          display: block !important;
        }
        
        .ag-theme-quartz .ag-cell.truncate-cell {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: 100% !important;
        }
        
        .ag-theme-quartz .ag-cell.truncate-cell .ag-cell-value {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: 100% !important;
          display: block !important;
        }
        .ag-checkbox-header {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .ag-theme-quartz .ag-header {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }
        .ag-theme-quartz .ag-header-cell.custom-header {
          background: #f9fafb;
          color: #243837;
          font-weight: 700;
          font-size: 14px;
          border-right: 1px solid #e5e7eb;
          text-align: center;
        }
        .ag-theme-quartz .ag-header-cell:last-child.custom-header {
          border-right: none;
        }
        .ag-theme-quartz .ag-row {
          border: none !important;
        }
        .ag-theme-quartz .ag-cell {
          border: none !important;
          font-size: 14px;
          background: #fff;
          padding: 0 12px;
          display: flex;
          align-items: center;
        }
        .ag-theme-quartz .ag-row-hover .ag-cell,
        .ag-theme-quartz .ag-row:hover .ag-cell {
          background: #f0f1f3 !important;
        }
        .ag-theme-quartz .ag-header-cell-text {
          overflow: visible;
          white-space: nowrap;
        }
        .ag-truncate {
          display: block;
          width: 100%;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
        /* Video aspect ratio enforcement */
        .video-container {
          width: 100% !important;
          height: 100% !important;
          overflow: hidden !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .video-container video {
          aspect-ratio: 9/16 !important;
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
        }
        
        /* Video controls overlay */
        .video-controls-overlay {
          pointer-events: auto;
        }
        
        .video-controls-overlay * {
          pointer-events: auto;
        }
        
        /* Fullscreen video styles */
        video:fullscreen {
          object-fit: contain !important;
          aspect-ratio: 9/16 !important;
          width: auto !important;
          height: 100vh !important;
        }
        video:-webkit-full-screen {
          object-fit: contain !important;
          aspect-ratio: 9/16 !important;
          width: auto !important;
          height: 100vh !important;
        }
        video:-moz-full-screen {
          object-fit: contain !important;
          aspect-ratio: 9/16 !important;
          width: auto !important;
          height: 100vh !important;
        }
        
        /* Modal video styles */
        .fullscreen-modal video {
          aspect-ratio: 9/16 !important;
          object-fit: contain !important;
          max-height: 90vh !important;
          max-width: 90vw !important;
          width: auto !important;
          height: auto !important;
        }
        
        /* Prevent background video interaction when modal is open */
        .video-disabled {
          pointer-events: none !important;
          opacity: 0.5 !important;
        }
        
        /* Ensure button is clickable */
        .further-insights-button {
          pointer-events: auto !important;
          cursor: pointer !important;
          position: relative !important;
          z-index: 30 !important;
        }
        
        .further-insights-button:hover {
          opacity: 0.9 !important;
        }
        
        /* Expandable card styles */
        .expandable-card {
          transition: all 0.3s ease !important;
        }
        
        .expandable-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
        
        .expandable-card.expanded {
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
        }
        
        /* Shepherd tour custom button styles */
        .shepherd-custom-buttons .shepherd-button {
          background-color: #B1FA63 !important;
          color: #243837 !important;
          border: 1px solid #B1FA63 !important;
          border-radius: 6px !important;
          padding: 8px 16px !important;
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
        }
        
        .shepherd-custom-buttons .shepherd-button:hover {
          background-color: #A3E635 !important;
          border-color: #A3E635 !important;
          transform: translateY(-1px) !important;
        }
        
        .shepherd-custom-buttons .shepherd-button:focus {
          outline: none !important;
          box-shadow: 0 0 0 2px rgba(177, 250, 99, 0.3) !important;
        }
        
        /* Loading overlay animations */
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        
        /* Compact AG Grid Requirements Table Styles */
        .requirements-table.ag-theme-quartz {
          --ag-font-size: 13px;
          --ag-header-height: 35px;
          --ag-row-height: 45px;
          --ag-border-color: #e5e7eb;
          --ag-header-background-color: #f9fafb;
          --ag-odd-row-background-color: #ffffff;
          --ag-even-row-background-color: #f9fafb;
          --ag-row-hover-color: #f0f1f3;
        }
        
        .requirements-table .ag-header-cell {
          font-weight: 600;
          font-size: 12px;
          color: #243837;
          padding: 0 8px;
        }
        
        .requirements-table .ag-cell {
          font-size: 13px;
          padding: 4px 8px;
          line-height: 1.4;
        }
        
        .requirements-table .ag-row {
          border-bottom: 1px solid #e5e7eb;
        }
        
        .requirements-table .ag-row:hover {
          background-color: #f0f1f3 !important;
        }
        
        .requirements-table .ag-paging-panel {
          padding: 8px;
          font-size: 12px;
          border-top: 1px solid #e5e7eb;
        }
        
        .requirements-table .ag-paging-button {
          padding: 4px 8px;
          font-size: 12px;
        }
        
        /* Custom Video Player Styles */
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }
        
        /* Range thumb styles */
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #243837;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #243837;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }
        
        /* Video player hover effects */
        .video-controls-button:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }
        
        /* Submission Table Styles */
        .submission-table.ag-theme-quartz {
          --ag-font-size: 13px;
          --ag-header-height: 40px;
          --ag-row-height: 40px;
          --ag-border-color: #e5e7eb;
          --ag-header-background-color: #f9fafb;
          --ag-odd-row-background-color: #ffffff;
          --ag-even-row-background-color: #f9fafb;
          --ag-row-hover-color: #f0f1f3;
          display: flex;
          flex-direction: column;
          height: 100% !important;
          overflow: hidden;
        }
        
        .submission-table.ag-theme-quartz .ag-root-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          height: 100% !important;
          min-height: 0;
          overflow: hidden;
        }
        
        .submission-table.ag-theme-quartz .ag-body-viewport {
          flex: 1;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          min-height: 0;
        }
        
        .submission-table.ag-theme-quartz .ag-center-cols-container {
          min-height: 0;
        }
        
        /* Hide pagination panel */
        .submission-table.ag-theme-quartz .ag-paging-panel {
          display: none !important;
        }
        
        .submission-table.ag-theme-quartz .ag-body-horizontal-scroll {
          display: none !important;
        }
        
        .submission-table.ag-theme-quartz .ag-cell {
          padding: 6px 8px !important;
        }
        
        .submission-table.ag-theme-quartz .ag-header-cell {
          padding: 8px !important;
        }
        
        .submission-table .ag-header-cell {
          font-weight: 600;
          font-size: 13px;
          color: #243837;
          padding: 0 12px;
        }
        
        .submission-table .ag-cell {
          font-size: 14px;
          padding: 12px;
          line-height: 1.5;
          display: flex;
          align-items: center;
        }
        
        /* Requirement column truncation */
        .submission-table .ag-cell[col-id="requirement"] {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          max-width: 100% !important;
        }
        
        .submission-table .ag-row {
          border-bottom: 1px solid #e5e7eb;
        }
        
        .submission-table .ag-row-height {
          height: 40px !important;
        }
        
        /* Custom scrollbar styling */
        .submission-table.ag-theme-quartz .ag-body-viewport::-webkit-scrollbar {
          width: 8px;
        }
        
        .submission-table.ag-theme-quartz .ag-body-viewport::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .submission-table.ag-theme-quartz .ag-body-viewport::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .submission-table.ag-theme-quartz .ag-body-viewport::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        .submission-table .ag-cell-edit-input {
          font-size: 14px;
          padding: 8px;
          border: 2px solid #B2A1FF;
          border-radius: 4px;
        }
        
        /* AG Grid tooltip styling */
        .submission-table .ag-tooltip {
          max-width: 400px;
          word-wrap: break-word;
          white-space: normal;
          background: #1f2937;
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 13px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          z-index: 10000;
        }
        
        /* Compliance column hover */
        .submission-table .ag-cell[col-id="compliance"] {
          cursor: help;
        }
        
        .submission-table .ag-cell[col-id="compliance"] svg {
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default VideoResult;